<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import useSubscriber from '../../../../Composables/Subscriber/useSubscriber.ts'
import useAppRouter from '../../../../Composables/useAppRouter.ts'
import { AlertType } from '../../../../Constants/AlertType.ts'
import useConfig from '../../../../Composables/useConfig'
import Alert from '../UI/Alert.vue'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import ChangePasswordModal from './ChangePasswordModal.vue'

// Composables
const { t, te } = useI18n()
const { hasFeature } = useConfig()
const { getQueryParameter } = useAppRouter()
const { credentialLoginAllowed } = useSubscriber()

// Ref
const changePasswordModal = ref<InstanceType<typeof ChangePasswordModal>>()

// Computed
const siret = computed<string | null>(() => getQueryParameter('siret'))
const email = computed<string | null>(() => getQueryParameter('email'))
const password = computed<string | null>(() => getQueryParameter('password'))
const errorMessage = computed<string | null>(() => {
  const errorCode = getQueryParameter('e')
  if (errorCode === null) {
    return null
  }

  if (te(`security.error.${errorCode}`)) {
    return t(`security.error.${errorCode}`)
  }

  return t(`security.error.bad_request`)
})

// Methods
function openChangePassword() {
  changePasswordModal.value?.initModal()
}
</script>

<template>
  <h3 v-if="te('login.connection_title')">
    {{ t('login.connection_title') }}
  </h3>

  <Alert v-if="errorMessage" :type="AlertType.ERROR">
    <template #title>
      <h5>{{ errorMessage }}</h5>
    </template>

    {{ t('security.error.try_again') }}
  </Alert>

  <form v-if="credentialLoginAllowed" action="/_connexion.php" method="POST">
    <input type="hidden" name="s" :value="siret" />

    <div class="form-group">
      <label v-if="te('login.email')" for="login-email">
        {{ t('login.email') }}
      </label>
      <input
        type="text"
        class="form-control form-control-lg"
        required
        id="login-email"
        name="email"
        :value="email"
        :placeholder="t('login.enter_email')"
        e2e-input="email"
      />
      <div class="invalid-feedback"></div>
    </div>

    <div class="form-group">
      <label v-if="te('login.password')" for="login-password">
        {{ t('login.password') }}
      </label>
      <input
        type="password"
        class="form-control form-control-lg"
        required
        id="login-password"
        name="password"
        :value="password"
        autocomplete="off"
        :placeholder="t('login.enter_password')"
        e2e-input="password"
      />

      <div class="invalid-feedback"></div>
    </div>

    <ButtonAll type="validation" width="full" margin="no" label="login.connexion" e2e-button="login" submit />

    <ButtonAll
      type="link"
      width="full"
      margin="no"
      class="text-muted"
      label="login.forgotten_password"
      e2e-button="openChangePasswordModal"
      @click="openChangePassword"
    />
  </form>

  <template v-if="hasFeature('connect_sso')">
    <ButtonAll
      type="validation"
      width="full"
      class="mt-4"
      margin="no"
      label="login.connexion_sso"
      href="_connexion-sso.php"
      e2e-button="loginSSO"
    />
  </template>

  <ChangePasswordModal v-if="credentialLoginAllowed" ref="changePasswordModal" />
</template>
